import { useEffect, useRef } from 'react';
import { ScanBuffer, type ScanBufferOptions } from './barcodeScan';

/**
 * Wires ScanBuffer's timing heuristic (see barcodeScan.ts for the
 * reasoning and its caveats) to real keydown events for as long as the
 * calling component is mounted and `enabled`. Calls `onScan(code)` the
 * instant a fast-typed, Enter/Tab-terminated run of characters is
 * recognised as a scan.
 *
 * Listens on `window`, not a specific input: a keyboard-wedge scanner
 * dumps its keystrokes into whatever element already has focus, and a
 * cashier scanning items is not expected to click into a text box first
 * (Phase 6 brief SS21). To avoid swallowing a scan-shaped burst while
 * someone is legitimately typing into an unrelated field - a discount
 * amount, a void reason, a sign-in password - capture is skipped whenever
 * the focused element is a text input/textarea/contenteditable, unless
 * that element opts in with a `data-scan-target` attribute (the POS
 * search box does, so scanning still works if the cashier's cursor
 * happens to be sitting in it).
 */
export function useBarcodeScan(
  onScan: (code: string) => void,
  options?: ScanBufferOptions & { enabled?: boolean },
): void {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const enabled = options?.enabled ?? true;
  // Individual timing knobs are read once per mount via `options` below;
  // callers pass stable values (module-level constants or memoised), same
  // convention as the rest of this codebase's effect dependencies.

  useEffect(() => {
    if (!enabled) return undefined;

    const buffer = new ScanBuffer(options);

    const isBlockedField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      if (target.dataset.scanTarget !== undefined) return false;
      return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey) {
        buffer.reset();
        return;
      }
      if (isBlockedField(event.target)) {
        buffer.reset();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (buffer.isEmpty) return;
        const code = buffer.finish(event.timeStamp);
        if (code) {
          event.preventDefault();
          onScanRef.current(code);
        }
        return;
      }
      if (event.key.length === 1) {
        buffer.push(event.key, event.timeStamp);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `options` is a
    // config object passed fresh each render by convention elsewhere in
    // this file's callers; only `enabled` toggles the listener itself.
  }, [enabled]);
}
